import * as THREE from 'three';

export type CameraMode = 'overhead' | 'cockpit';

export class CameraRig {
  private readonly desiredPosition = new THREE.Vector3();
  private readonly lookTarget = new THREE.Vector3();
  private readonly localOffset = new THREE.Vector3();
  private readonly localLook = new THREE.Vector3();
  private readonly yawRotation = new THREE.Quaternion();
  private mode: CameraMode = 'overhead';
  private shake = 0;

  constructor(
    private readonly camera: THREE.PerspectiveCamera,
    private readonly overheadOffset = new THREE.Vector3(0, 9.5, 9.5),
  ) {}

  get currentMode(): CameraMode { return this.mode; }

  impulse(amount: number): void { this.shake = Math.min(0.7, this.shake + Math.max(0, amount)); }

  setMode(mode: CameraMode, target: THREE.Vector3, yaw: number): void {
    this.mode = mode;
    this.snapTo(target, yaw);
  }

  snapTo(target: THREE.Vector3, yaw = 0): void {
    this.calculatePose(target, yaw);
    this.camera.position.copy(this.desiredPosition);
    this.camera.fov = this.mode === 'cockpit' ? 64 : 48;
    this.camera.updateProjectionMatrix();
    this.camera.lookAt(this.lookTarget);
  }

  update(delta: number, target: THREE.Vector3, yaw: number, lag: number): void {
    this.calculatePose(target, yaw);
    const factor = 1 - Math.exp(-delta / Math.max(0.001, lag));
    // In cockpit view the player's car occupies the foreground. Any camera
    // lag makes that nearby body slide against the view on every physics step,
    // which reads as vibration. Lock the camera to the car pose; retain lag
    // only for the detached overhead camera.
    if (this.mode === 'cockpit') this.camera.position.copy(this.desiredPosition);
    else this.camera.position.lerp(this.desiredPosition, factor);
    if (this.shake > 0.002 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const time = performance.now() * 0.035;
      this.camera.position.x += Math.sin(time * 1.7) * this.shake;
      this.camera.position.y += Math.cos(time * 1.3) * this.shake * 0.45;
      this.camera.position.z += Math.sin(time * 2.1) * this.shake * 0.6;
      this.shake *= Math.exp(-delta * 12);
    } else this.shake = 0;
    this.camera.fov = THREE.MathUtils.lerp(this.camera.fov, this.mode === 'cockpit' ? 64 : 48, factor);
    this.camera.updateProjectionMatrix();
    this.camera.lookAt(this.lookTarget);
  }

  private calculatePose(target: THREE.Vector3, yaw: number): void {
    if (this.mode === 'overhead') {
      this.desiredPosition.copy(target).add(this.overheadOffset);
      this.lookTarget.copy(target).add(this.localLook.set(0, 0.35, -1.2));
      return;
    }

    this.yawRotation.setFromAxisAngle(THREE.Object3D.DEFAULT_UP, yaw);
    // The imported car is about 1.33 units tall and faces local -Z. Place the
    // camera just above and behind its cockpit so the hood remains visible
    // without putting the near plane inside the bodywork.
    this.localOffset.set(-0.38, 1.34, 0.92).applyQuaternion(this.yawRotation);
    this.localLook.set(-0.15, 0.5, -8).applyQuaternion(this.yawRotation);
    this.desiredPosition.copy(target).add(this.localOffset);
    this.lookTarget.copy(target).add(this.localLook);
  }
}
